import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react'
import {
  DreamityGoalResDto,
  DreamityGoalSaveReqDto,
  DreamityGoalModifyReqDto,
} from '../types/goal.types'
import { DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT } from '@/services/dreamityLocalSnapshot'
import { dreamityPlannerRepository } from '@/services/dreamityPlannerRepository'
import { scheduleDebouncedPushDreamitySnapshot } from '@/services/dreamitySyncService'

interface GoalState {
  goals: DreamityGoalResDto[]
  selectedGoal: DreamityGoalResDto | null
  loading: boolean
  error: string | null
}

type GoalAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_GOALS'; payload: DreamityGoalResDto[] }
  | { type: 'SET_SELECTED_GOAL'; payload: DreamityGoalResDto | null }
  | { type: 'UPDATE_GOAL_IN_LIST'; payload: DreamityGoalResDto }
  | { type: 'DELETE_GOAL'; payload: string }

interface GoalContextType extends GoalState {
  fetchGoals: (dreamId: string, force?: boolean) => Promise<void>
  createGoal: (goal: DreamityGoalSaveReqDto) => Promise<void>
  updateGoal: (goalId: string, goal: DreamityGoalModifyReqDto) => Promise<void>
  deleteGoal: (goalId: string) => Promise<void>
  selectGoal: (goal: DreamityGoalResDto | null) => void
}

const GoalContext = createContext<GoalContextType | undefined>(undefined)

const initialState: GoalState = {
  goals: [],
  selectedGoal: null,
  loading: false,
  error: null,
}

function goalReducer(state: GoalState, action: GoalAction): GoalState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_GOALS':
      return { ...state, goals: action.payload }
    case 'SET_SELECTED_GOAL':
      return { ...state, selectedGoal: action.payload }
    case 'UPDATE_GOAL_IN_LIST':
      return {
        ...state,
        goals: state.goals.map((goal) =>
          goal.goalId === action.payload.goalId ? action.payload : goal
        ),
        selectedGoal:
          state.selectedGoal?.goalId === action.payload.goalId
            ? action.payload
            : state.selectedGoal,
      }
    case 'DELETE_GOAL':
      return {
        ...state,
        goals: state.goals.filter((goal) => goal.goalId !== action.payload),
        selectedGoal:
          state.selectedGoal?.goalId === action.payload ? null : state.selectedGoal,
      }
    default:
      return state
  }
}

interface GoalProviderProps {
  children: ReactNode
}

export function GoalProvider({ children }: GoalProviderProps) {
  const [state, dispatch] = useReducer(goalReducer, initialState)
  const lastFetchedDreamRef = useRef<string | null>(null)
  const ongoingFetchRef = useRef<Promise<void> | null>(null)

  const fetchGoals = useCallback(async (dreamId: string, force: boolean = false) => {
    if (!force && lastFetchedDreamRef.current === dreamId && state.goals.length > 0) {
      return
    }

    if (ongoingFetchRef.current) {
      try {
        await ongoingFetchRef.current
        return
      } catch {
        /* retry */
      }
    }

    dispatch({ type: 'SET_ERROR', payload: null })

    const promise = (async () => {
      try {
        const goals = dreamityPlannerRepository.listGoals(dreamId)
        dispatch({ type: 'SET_GOALS', payload: goals })
        lastFetchedDreamRef.current = dreamId
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
        lastFetchedDreamRef.current = null
        throw error
      }
    })()

    ongoingFetchRef.current = promise
    try {
      await promise
    } finally {
      ongoingFetchRef.current = null
    }
  }, [state.goals.length])

  const createGoal = async (goal: DreamityGoalSaveReqDto) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      dreamityPlannerRepository.createGoal(goal)
      dispatch({ type: 'SET_GOALS', payload: dreamityPlannerRepository.listGoals(goal.dreamId) })
      lastFetchedDreamRef.current = goal.dreamId
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  const updateGoal = async (goalId: string, goal: DreamityGoalModifyReqDto) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const goalToUpdate = state.goals.find((g) => g.goalId === goalId)
      if (!goalToUpdate) {
        throw new Error('해당 목표를 찾을 수 없습니다.')
      }
      dreamityPlannerRepository.updateGoal(goalId, goal)
      dispatch({
        type: 'SET_GOALS',
        payload: dreamityPlannerRepository.listGoals(goalToUpdate.dreamId),
      })
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const deleteGoal = async (goalId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const goalToDelete = state.goals.find((g) => g.goalId === goalId)
      if (!goalToDelete) {
        throw new Error('해당 목표를 찾을 수 없습니다.')
      }
      dreamityPlannerRepository.deleteGoal(goalId)
      dispatch({ type: 'DELETE_GOAL', payload: goalId })
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  const selectGoal = (goal: DreamityGoalResDto | null) => {
    dispatch({ type: 'SET_SELECTED_GOAL', payload: goal })
  }

  useEffect(() => {
    const onSnapshotReplaced = () => {
      const dreamId = lastFetchedDreamRef.current
      if (!dreamId) return
      try {
        const goals = dreamityPlannerRepository.listGoals(dreamId)
        dispatch({ type: 'SET_GOALS', payload: goals })
      } catch {
        /* ignore */
      }
    }
    window.addEventListener(DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT, onSnapshotReplaced)
    return () => {
      window.removeEventListener(DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT, onSnapshotReplaced)
    }
  }, [])

  return (
    <GoalContext.Provider
      value={{
        ...state,
        fetchGoals,
        createGoal,
        updateGoal,
        deleteGoal,
        selectGoal,
      }}
    >
      {children}
    </GoalContext.Provider>
  )
}

export function useGoalContext() {
  const context = useContext(GoalContext)
  if (context === undefined) {
    throw new Error('useGoal must be used within a GoalProvider')
  }
  return context
}
